import React, { useState } from 'react';
import { FaCreditCard, FaTrash, FaLock, FaUnlock } from 'react-icons/fa';
import { decryptData } from '../utils/encryption';

const CardGradients = {
  VISA: 'from-blue-600 to-blue-800',
  MASTERCARD: 'from-red-600 to-red-800',
  AMEX: 'from-green-600 to-green-800',
  DISCOVER: 'from-orange-600 to-orange-800',
  DEFAULT: 'from-gray-700 to-gray-900'
};

const CreditCard = ({ data, onDelete, onView }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [decryptedData, setDecryptedData] = useState(null);
  const [error, setError] = useState('');
  const [showSecretKeyDialog, setShowSecretKeyDialog] = useState(false);
  const [isForDelete, setIsForDelete] = useState(false);

  const formatCardNumber = (number) => {
    return number?.match(/.{1,4}/g)?.join(' ') || '•••• •••• •••• ••••';
  };

  // Fixed getCardGradient function
  const getCardGradient = () => {
    const cardTitle = data?.title || '';
    const variant = cardTitle.includes('VISA') ? 'VISA' 
      : cardTitle.includes('MASTERCARD') ? 'MASTERCARD'
      : cardTitle.includes('AMEX') ? 'AMEX'
      : cardTitle.includes('DISCOVER') ? 'DISCOVER'
      : 'DEFAULT';
    return CardGradients[variant];
  };

  const formatExpiryDate = (expiryDate) => {
    if (!expiryDate) return '••/••';
    // Split the expiryDate if it's in format "MM/YY" or "MM/YYYY"
    const [month, year] = expiryDate.split('/');
    // Format month and year
    const formattedMonth = month.padStart(2, '0');
    const formattedYear = year.length === 4 ? year.slice(-2) : year;
    return `${formattedMonth}/${formattedYear}`;
  };

  const truncateCardHolder = (name) => {
    if (!name) return '';
    return name.length > 20 ? name.substring(0, 17) + '...' : name;
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!decryptedData) {
      setIsFlipped(true);
      setSecretKey('');
      setError('');
    }
  };

  const handleKeySubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Attempting to decrypt with data:', data);
      // Fixed to access the correct property
      const encryptedData = data.encryptedData;
      if (!encryptedData) {
        throw new Error('No encrypted data found');
      }

      const decrypted = await decryptData(encryptedData, secretKey);
      console.log(decrypted);
      if (decrypted) {
        setDecryptedData(decrypted);
        setError('');
      } else {
        throw new Error('Decryption failed');
      }
    } catch (err) {
      console.error('Decryption error:', err);
      setError('Invalid secret key or corrupted data');
      setSecretKey('');
    }
  };

  const handleLockCard = () => {
    setIsFlipped(false);
    setDecryptedData(null);
    setSecretKey('');
    setError('');
  };

  const handleDelete = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsForDelete(true);
    setShowSecretKeyDialog(true);
  };

  const handleView = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsForDelete(false);
    setShowSecretKeyDialog(true);
  };

  const handleSecretKeySubmit = (secretKey) => {
    if (isForDelete) {
      onDelete && onDelete(data._id, secretKey);
    } else {
      onView && onView(data, secretKey);
    }
    setShowSecretKeyDialog(false);
  };

  return (
    <div className="w-full">
      <div className="w-full max-w-[420px] min-w-[300px] mx-auto aspect-[1.586/1]">
        <div className="relative w-full h-full perspective">
          <div 
            onClick={handleCardClick}
            className={`absolute inset-0 transition-transform duration-700 transform-style-3d cursor-pointer
              ${isFlipped ? 'rotate-y-180' : ''}`}
          >
            {/* Front of card */}
            <div className={`absolute inset-0 w-full h-full bg-gradient-to-r ${getCardGradient()} 
              text-white rounded-xl p-6 shadow-lg backface-hidden`}>
              
              {/* Card Name and Logo */}
              <div className="flex justify-between items-start mb-4">
                <div className="text-lg font-medium truncate max-w-[200px]">
                  {data?.data?.cardName || 'Credit Card'}
                </div>
                <FaCreditCard className="text-3xl opacity-80" />
              </div>

              {/* Chip */}
              <div className="w-12 h-9 bg-yellow-300 rounded-md opacity-80 mb-6"></div>

              {/* Card Number */}
              <div className="mb-6">
                <div className="text-sm opacity-70 mb-1">Card Number</div>
                <div className="font-mono text-lg tracking-[0.25em]">
                  •••• •••• •••• ••••
                </div>
              </div>

              {/* Updated Card Details Grid */}
              <div className="grid grid-cols-12 gap-4">
                {/* Card Holder - spans 7 columns */}
                <div className="col-span-7">
                  <div className="text-sm opacity-70 mb-1">Card Holder</div>
                  <div className="font-medium uppercase tracking-wider text-sm">
                    ••••••••
                  </div>
                </div>

                {/* Expiry - spans 3 columns */}
                <div className="col-span-3">
                  <div className="text-sm opacity-70 mb-1">Expires</div>
                  <div className="font-medium text-sm">
                    ••/••
                  </div>
                </div>

                {/* CVV - spans 2 columns */}
                <div className="col-span-2">
                  <div className="text-xs opacity-70 mb-1">CVV</div>
                  <div className="font-medium text-sm bg-white/10 px-2 py-1 rounded">
                    •••
                  </div>
                </div>
              </div>

              {/* Card Type/Variant */}
              <div className="absolute bottom-6 right-6 text-sm font-medium opacity-70">
                {data?.data?.variant || 'CREDIT'}
              </div>
            </div>

            {/* Back of card */}
            <div className={`absolute inset-0 w-full h-full bg-gradient-to-r from-gray-800 to-gray-900 
              text-white rounded-xl p-6 backface-hidden rotate-y-180`}>
              {!decryptedData ? (
                <form onSubmit={handleKeySubmit} className="h-full flex flex-col justify-center items-center">
                  <FaLock className="text-4xl mb-4 opacity-80" />
                  <input
                    type="password"
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder="Enter secret key"
                    className="w-full max-w-xs p-2 rounded bg-white/10 border border-white/20 
                      text-white placeholder-white/50 mb-4"
                    autoFocus
                    onClick={(e) => e.stopPropagation()} // Prevent card flip when typing
                  />
                  {error && <div className="text-red-400 text-sm mb-4">{error}</div>}
                  <button
                    type="submit"
                    onClick={(e) => e.stopPropagation()} // Prevent card flip when clicking button
                    className="px-4 py-2 bg-white/10 rounded hover:bg-white/20 transition-colors"
                  >
                    Unlock
                  </button>
                </form>
              ) : (
                <div className="h-full flex flex-col">
                  {/* Card Name */}
                  <div className="mb-4 text-lg font-medium truncate">
                    {decryptedData?.cardName}
                  </div>

                  {/* Card Number */}
                  <div className="mb-4">
                    <div className="text-sm opacity-70 mb-1">Card Number</div>
                    <div className="font-mono text-lg tracking-wider">
                      {formatCardNumber(decryptedData?.cardNumber)}
                    </div>
                  </div>

                  {/* Updated Card Details Layout */}
                  <div className="grid grid-cols-12 gap-4 mb-auto">
                    {/* Card Holder - spans 7 columns */}
                    <div className="col-span-7">
                      <div className="text-sm opacity-70 mb-1">Card Holder</div>
                      <div className="font-medium uppercase tracking-wider truncate text-sm">
                        {truncateCardHolder(decryptedData?.cardHolderName)}
                      </div>
                    </div>

                    {/* Expiry - spans 3 columns */}
                    <div className="col-span-3">
                      <div className="text-sm opacity-70 mb-1">Expires</div>
                      <div className="font-medium text-sm">
                        {formatExpiryDate(decryptedData?.expiryDate)}
                      </div>
                    </div>

                    {/* CVV - spans 2 columns */}
                    <div className="col-span-2">
                      <div className="text-xs opacity-70 mb-1">CVV</div>
                      <div className="font-medium text-sm bg-gray-700 px-2 py-1 rounded">
                        {decryptedData?.cvv}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons with updated spacing */}
                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/10">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLockCard();
                      }}
                      className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity text-sm"
                    >
                      <FaUnlock className="w-3 h-3" /> Lock Card
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="text-red-400 hover:text-red-300 transition-colors text-sm"
                    >
                      Delete Card
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Title */}
      <div className="text-center mt-3 text-gray-700 font-medium">
        {data?.data?.cardName || 'Credit Card'}
      </div>
    </div>
  );
};

export default CreditCard;
